/**
 * OTP Verification Page
 * Enter and verify OTP code
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';

const OTPVerificationPage = () => {
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [canResend, setCanResend] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const email = location.state?.email;
  const phoneNumber = location.state?.phoneNumber; // Keep for backward compatibility
  const identifier = email || phoneNumber; // Use email if available, fallback to phone
  const isNewRegistration = location.state?.isNewRegistration || false;
  const otpSent = location.state?.otpSent !== false; // Default to true for backward compatibility
  const otpError = location.state?.otpError || null;

  // Redirect if no identifier
  useEffect(() => {
    if (!identifier) {
      navigate('/login');
    }
  }, [identifier, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsLoading(true);

    try {
      let result;
      // Use email-based verification if email is available, otherwise use phone
      if (email) {
        result = await login(email, otp, 'email');
      } else {
        result = await login(phoneNumber, otp, 'phone');
      }

      if (result.success) {
        // Redirect to role-specific dashboard
        const role = result.user?.role;
        switch (role) {
          case 'CANDIDATE':
            navigate('/candidate/dashboard');
            break;
          case 'HR':
            navigate('/hr/dashboard');
            break;
          case 'ADMIN':
            navigate('/admin/dashboard');
            break;
          case 'CHAIRMAN':
            navigate('/chairman/dashboard');
            break;
          default:
            navigate('/');
        }
      } else {
        setError(result.error || 'Invalid OTP');
        setOtp('');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
      setOtp('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setCanResend(false);
    setTimeLeft(300);

    try {
      if (email) {
        await authService.sendEmailOTP(email);
      } else {
        await authService.sendOTP(phoneNumber);
      }
      // Show success message (optional)
    } catch (err) {
      setError('Failed to resend OTP');
      setCanResend(true);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 6) {
      setOtp(value);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-background p-lg relative overflow-hidden
      before:content-[''] before:absolute before:top-[-10%] before:right-[-5%] before:w-[350px] before:h-[350px] 
      before:bg-accent-terracotta before:rounded-full before:opacity-[0.06] before:animate-[floatSlow_28s_ease-in-out_infinite] before:z-0
      after:content-[''] after:absolute after:bottom-[-10%] after:left-[-5%] after:w-[300px] after:h-[300px] 
      after:bg-accent-green after:rounded-full after:opacity-[0.06] after:animate-[floatSlow_32s_ease-in-out_infinite_reverse] after:z-0">
      <div className="bg-white p-2xl rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08)] w-full max-w-[420px] 
        relative z-[1] border border-[rgba(46,46,46,0.08)] animate-slideUp">
        {isNewRegistration && (
          <div className="bg-accent-green text-white p-4 rounded-md mb-lg text-center font-medium text-[14px]">
            Registration successful! Please verify your email address.
          </div>
        )}

        {!otpSent && otpError && (
          <div className="bg-orange-50 border-l-4 border-orange-500 text-orange-700 p-4 rounded-md mb-lg">
            <p className="font-semibold text-sm mb-1">⚠️ OTP Not Sent</p>
            <p className="text-xs mb-2">{otpError}</p>
            <p className="text-xs">OTP service unavailable. Please try again shortly.</p>
          </div>
        )}

        <h2 className="text-[28px] text-primary mb-sm text-center font-semibold tracking-[-0.5px]">
          Enter Verification Code
        </h2>
        <p className="text-center text-text-secondary mb-xl text-[14px] leading-[1.5]">
          {otpSent ? (
            <>We sent a code to <strong>{email || phoneNumber}</strong></>
          ) : (
            <>Enter OTP for <strong>{email || phoneNumber}</strong></>
          )}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-lg">
            <input
              type="text"
              value={otp}
              onChange={handleOtpChange}
              placeholder="000000"
              className="w-full py-[18px] px-4 border-[1.5px] border-border rounded-md text-[32px] 
                text-center tracking-[10px] font-semibold transition-all duration-200 box-border 
                bg-surface text-primary mb-md
                focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isLoading}
              autoFocus
              maxLength={6}
            />
            <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
              Enter the 6-digit code sent to your {email ? 'email' : 'phone'}
            </small>
          </div>

          {error && (
            <div className="bg-status-error-bg text-status-error py-[14px] px-4 rounded-md mb-lg 
              text-[14px] border-l-[3px] border-status-error leading-[1.5]">
              {error}
            </div>
          )}

          <div className="text-center my-md text-text-secondary text-[14px] font-medium">
            {timeLeft > 0 ? (
              <span>Code expires in {formatTime(timeLeft)}</span>
            ) : (
              <span className="text-status-error font-semibold">Code expired</span>
            )}
          </div>

          <button
            type="submit"
            className="w-full py-[14px] px-6 bg-accent-green text-surface border-none rounded-md 
              text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
              hover:bg-accent-green-dark hover:-translate-y-[1px] hover:shadow-md hover:enabled:bg-accent-green-dark
              active:translate-y-0 active:enabled:translate-y-0
              disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            disabled={isLoading || otp.length !== 6}
          >
            {isLoading ? 'Verifying...' : 'Verify OTP'}
          </button>
        </form>

        <div className="text-center my-lg">
          {canResend ? (
            <button
              onClick={handleResend}
              className="bg-transparent border-none text-accent-green cursor-pointer text-[14px] 
                font-medium py-sm px-md inline-block my-sm mx-auto rounded-sm transition-all duration-200
                hover:text-accent-green-dark hover:bg-[rgba(107,127,92,0.05)]"
            >
              Resend Code
            </button>
          ) : (
            <span className="text-text-tertiary text-[14px]">
              Resend available in {formatTime(timeLeft)}
            </span>
          )}
        </div>

        <button
          onClick={() => navigate('/login')}
          className="bg-transparent border-none text-accent-green cursor-pointer text-[14px] 
            font-medium py-sm px-md inline-block my-sm mx-auto rounded-sm transition-all duration-200
            hover:text-accent-green-dark hover:bg-[rgba(107,127,92,0.05)]"
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );
};

export default OTPVerificationPage;
